import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '../../../components/Brand/Icon';
import { useDisclosureTransition } from './useDisclosureTransition';

// One frame, shared edges. The country and language controls use the same
// keyboard model and never inherit Ant Design's separate input/portal borders.
export default function SettingsPicker({ id, value, options, label, placeholder, compact = false,
  searchable = false, searchPlaceholder, emptyLabel, open, onOpenChange, onChange, disabled, busy }) {
  const root = useRef(null);
  const trigger = useRef(null);
  const search = useRef(null);
  const list = useRef(null);
  const menu = useDisclosureTransition(open);
  const [query, setQuery] = useState('');
  const normalizedQuery = query.toLocaleLowerCase();
  const visible = options.filter(item => `${item.label} ${item.value}`.toLocaleLowerCase().includes(normalizedQuery));
  const current = options.find(item => item.value === value);
  useEffect(() => { if (!menu.present) setQuery(''); }, [menu.present]);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const picker = root.current;
    const scroller = picker.closest('.settings-content');
    let frame = 0;
    const measure = () => {
      const viewport = window.visualViewport;
      const content = scroller?.getBoundingClientRect();
      const top = Math.max(content?.top || 0, viewport?.offsetTop || 0);
      const bottom = Math.min(content?.bottom || window.innerHeight, (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight));
      // Inline menus contribute to the root's height. Measuring that root
      // would let the placement depend on its own previous layout.
      const box = trigger.current.getBoundingClientRect();
      const below = Math.max(0, bottom - box.bottom - 4), above = Math.max(0, box.top - top - 4);
      // In a short keyboard viewport, keep the menu in the scroll flow.
      // Flipping it above/below during scrolling moves the option just as
      // the user reaches it, sometimes behind the scroller's clipping edge.
      const side = bottom - top < 264 || Math.max(above, below) < 132 ? 'inline'
        : below < 200 && above > below ? 'above' : 'below';
      picker.dataset.side = side;
      picker.style.setProperty('--picker-height', `${side === 'inline' ? 176 : Math.max(44, Math.min(320, side === 'above' ? above : below))}px`);
    };
    const schedule = event => {
      if (event?.target === list.current) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule);
    document.addEventListener('scroll', schedule, true);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    // The shell can finish measuring header/footer after visualViewport's
    // resize event, so observe the actual usable area too.
    if (scroller) observer?.observe(scroller);
    if (searchable) search.current?.focus({ preventScroll: true });
    else list.current?.querySelector(`[aria-selected='true']`)?.focus({ preventScroll: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('scroll', schedule);
      document.removeEventListener('scroll', schedule, true);
      observer?.disconnect();
    };
  }, [open, searchable]);
  const close = () => { onOpenChange(false); trigger.current?.focus({ preventScroll: true }); };
  const keyDown = event => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (!open || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    if (event.target === search.current && ['Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const buttons = [...list.current.querySelectorAll('[role=option]')];
    if (!buttons.length) return;
    const index = buttons.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
      : event.key === 'ArrowDown' ? (index + 1) % buttons.length : index < 0 ? buttons.length - 1 : (index - 1 + buttons.length) % buttons.length;
    buttons[next].focus({ preventScroll: true });
    buttons[next].scrollIntoView({ block: 'nearest' });
  };
  return <div ref={root} className={`settings-picker ${compact ? 'settings-language' : 'settings-country'}`}
    data-open={menu.present} onKeyDown={keyDown}>
    <button ref={trigger} id={id} type="button" className="settings-picker-trigger" role="combobox"
      aria-label={label} aria-expanded={open} aria-haspopup="listbox" aria-controls={`${id}-list`}
      aria-busy={busy || undefined} aria-disabled={busy || undefined} disabled={disabled}
      onClick={() => { if (!busy) onOpenChange(!open); }}
      onKeyDown={event => {
        if (!open && ['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); if (!busy) onOpenChange(true); }
      }}>
      <span className="settings-picker-value">
        {!compact && current?.flag && <span className="settings-country-flag" aria-hidden="true">{current.flag}</span>}
        <span>{current ? (compact ? current.shortLabel : current.label) : placeholder || label}</span>
      </span>
      {!compact && <ChevronDownIcon />}
    </button>
    {menu.present && <div ref={menu.ref} className="settings-picker-menu" aria-hidden={!open || undefined} inert={!open ? '' : undefined}>
      {searchable && <input ref={search} type="search" className="settings-country-query" aria-label={label}
        value={query} placeholder={searchPlaceholder || placeholder} onChange={event => setQuery(event.target.value)} />}
      <div id={`${id}-list`} role="listbox" aria-label={label} ref={list} className="settings-picker-options">
        {visible.map(item => <button type="button" key={item.value} role="option" aria-label={item.label}
          aria-selected={item.value === value} tabIndex={-1} disabled={busy} className="settings-picker-option"
          onClick={() => { close(); onChange(item.value); }}>
          {!compact && item.flag && <span className="settings-country-flag" aria-hidden="true">{item.flag}</span>}
          <span>{compact ? item.shortLabel : item.label}</span>
        </button>)}
        {!visible.length && <p className="settings-empty" role="status">{emptyLabel}</p>}
      </div>
    </div>}
  </div>;
}
